import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { IPermissionCheck, RoleDTO, PermissionCheck } from '@models/role';
import { AccountType, IUserDTO, UserDTO } from '@models/user';
import { Critter } from '@app/infrastructure/models/critter';

/**
 * Maintains active state listeners for authentication and role status.
 */
@Injectable({
  providedIn: 'root',
})
export class UserStateService {
  // Saving user session data in local storage temporarily until session data is revisited.
  public userSession$ = new BehaviorSubject<IUserDTO>(
    JSON.parse(localStorage.getItem('user')),
  );

  get userSession() {
    return this.userSession$.getValue();
  }

  public getUserSession(): Observable<IUserDTO> {
    return this.userSession$.asObservable();
  }

  public setUserSession(user: IUserDTO): void {
    localStorage.setItem('user', JSON.stringify(user));
    this.userSession$.next(user);
  }

  public resetUserSession(): void {
    this.userSession$.next(new UserDTO());
  }

  public get userAvatar(): Critter {
    const user = this.userSession;
    if (user?.student?.critter) {
      return user.student.critter;
    }
    return null;
  }

  public setUserAvatar(critter: Critter, charge: boolean = true): void {
    const user = this.userSession;
    if (user?.student) {
      if (charge) user.student.coinPurse -= 5;
      user.student.critter = critter;
      this.setUserSession(user);
    }
  }

  /**
   * Combined state stream to listen to multiple role states.
   */
  public checkUserPermissions(): Observable<IPermissionCheck> {
    return this.getUserSession().pipe(
      map((user) => {
        const roleName = user?.role?.roleName;
        const permissionList: IPermissionCheck = new PermissionCheck({
          canEdit: RoleDTO.canEdit(roleName),
          hasAcceptedStudentAgreements: !!(
            RoleDTO.isStudentRoleType(roleName) &&
            user?.dateOfGuardianConsent &&
            user?.dateOfGuardianPermission &&
            user?.dateOfTermsAgreement
          ),
          hasAcceptedTeacherAgreement: !!(
            RoleDTO.isTeacher(roleName) && user?.dateOfTermsAgreement
          ),
          isAdmin: RoleDTO.isAdminRoleType(roleName),
          isEnrolled:
            (RoleDTO.isStudentRoleType(roleName) && user.student?.isEnrolled) ||
            (RoleDTO.isValidRoleType(roleName) &&
              !RoleDTO.isStudentRoleType(roleName)),
          isLocalUser: user?.accountType === AccountType.LOCAL,
          isStudent: RoleDTO.isStudentRoleType(roleName),
          isSuperAdmin: RoleDTO.isSuperAdminRoleType(roleName),
          isTeacher: RoleDTO.isTeacher(roleName),
          isValid: RoleDTO.isValidRoleType(roleName),
        });
        return permissionList;
      }),
    );
  }

  public isSelf(userId: string): Observable<boolean> {
    return this.getUserSession().pipe(map((user) => user?.id === userId));
  }
}
